/** Thanks to https://abhiiishek07.medium.com/kanban-board-multi-list-drag-and-drop-with-react-beautiful-dnd-in-next-js-12b73c32e938 */

import { useEffect, useState } from "react";

/** Helpers */
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";

/** MUI */
import { Alert, Box, Collapse, Grid } from "@mui/material";

/** UI */
import Column from "./Column";

const INITIAL_COLUMN_ORDER = ["level"];

const INITIAL_COL_DATA = {
    level: {
        id: "level",
        title: "Poziomy",
        itemsOrder: [],
    },
};

const Board = (props) => {
    const { levels, choise, onReorderLevels, onChangeColumn, onChangeOrder, onRemoveLevel, onAddLevel } = props;

    const { t } = useTranslation();

    /** states */
    const [columnsOrder, setColumnsOrder] = useState(INITIAL_COLUMN_ORDER);
    const [data, setData] = useState(INITIAL_COL_DATA);
    const [items, setItems] = useState({});
    const [showNote, setShowNote] = useState(false);

    /** effects */
    useEffect(() => {
        const _newData = {
            level: {
                id: "level",
                title: "Poziomy",
                itemsOrder: levels?.map((l) => l.id),
            },
        };

        setData(_newData);

        const _newItems = {};

        levels?.forEach((level) => {
            _newItems[level.id] = level;
        });
        setItems(_newItems);
    }, [levels]);

    /** handlers */
    const handleOnFocused = ({ event, focused }) => {
        const _target = event?.target;
        const _currentTarget = event?.currentTarget;

        setShowNote(focused && _target === _currentTarget);
    };

    const handleDragStart = (start, provided, snapshot) => {
        const { draggableId: currentItem, source } = start;

        const { droppableId: from } = source;

        provided.announce(
            t(`Zaznaczono element {{itemName}} na pozycji {{startPosition}} z {{listLength}}{{dot}}`, {
                itemName: items?.[currentItem]?.title,
                startPosition: start.source.index + 1,
                listLength: data?.[from]?.itemsOrder?.length,
                listName: t(data?.[from]?.title),
                dot: ".",
            })
        );
    };

    const handleDragUpdate = (update, provided) => {
        const { draggableId: currentItem, source, destination } = update;
        const { droppableId: from } = source;
        const { droppableId: to } = destination || {};

        const message = update.destination
            ? t(`Przesunięto element {{itemName}} do pozycji {{startPosition}} z {{size}}{{dot}}`, {
                  itemName: items?.[currentItem]?.title,
                  startPosition: update.destination.index + 1,
                  listLength: data?.[to]?.itemsOrder?.length,
                  listName: t(data?.[to]?.title),
                  size: Object.keys(items || {})?.length,
                  dot: ".",
              })
            : t(`Obecnie nie jesteś na obszarze, na który można przeciągać{{dot}}`, { dot: "." });

        provided.announce(message);
    };

    const handleDragEnd = (results, provided) => {
        const { draggableId: currentItem, source, destination, type } = results;
        const { droppableId: from, index: fromIndex } = source;
        const { droppableId: to, index: toIndex } = destination || {};

        let message = "";

        if (from !== to || fromIndex !== toIndex) {
            message += t(`Przeniesiono {{itemName}} do pozycji {{toIndex}} z {{size}}{{dot}}`, {
                itemName: items?.[currentItem]?.title,
                fromIndex: fromIndex + 1,
                toIndex: toIndex + 1,
                listName: t(data?.[to]?.title),
                size: Object.keys(items || {})?.length,
                dot: ".",
            });
        } else {
            message += t("Odznaczono {{itemName}}{{dot}} ", { itemName: items?.[currentItem]?.title, dot: "." });
        }

        provided.announce(message);

        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        const sourceIndex = source.index;
        const destinationIndex = destination.index;

        if (type === "COLUMN") {
            //dragging the columns
            const reorderedColumns = [...columnsOrder];
            const [removedItem] = reorderedColumns.splice(sourceIndex, 1);
            reorderedColumns.splice(destinationIndex, 0, removedItem);

            setColumnsOrder(reorderedColumns);
            //save the reordered column in database

            return;
        } else {
            //changes within same column

            if (source.droppableId === destination.droppableId) {
                const source_col_id = source.droppableId;
                const new_items_id_collection = [...data[source_col_id].itemsOrder];
                const [deleted_item_id] = new_items_id_collection.splice(sourceIndex, 1);
                new_items_id_collection.splice(destinationIndex, 0, deleted_item_id);
                const new_data = { ...data };
                new_data[source_col_id].itemsOrder = new_items_id_collection;

                const _newLevels = [];
                new_data[source_col_id]?.itemsOrder?.forEach((itemId) => {
                    const _levelItem = levels?.find((l) => l.id === itemId);
                    _newLevels.push(_levelItem);
                });

                setData(new_data);

                onReorderLevels && onReorderLevels(_newLevels);

                //update the db
            } else {
                //changes within different col
                const source_col_id = source.droppableId,
                    dest_col_id = destination.droppableId;

                const new_source_items_id_collc = [...data[source_col_id].itemsOrder];
                const new_dest_items_id_collc = [...data[dest_col_id].itemsOrder];
                const [deleted_item_id] = new_source_items_id_collc.splice(sourceIndex, 1);

                new_dest_items_id_collc.splice(destinationIndex, 0, deleted_item_id);
                const new_data = { ...data };
                new_data[source_col_id].itemsOrder = new_source_items_id_collc;
                new_data[dest_col_id].itemsOrder = new_dest_items_id_collc;

                setData(new_data);

                //update the db
            }
        }
    };

    return (
        <Box sx={{ mt: 0 }}>
            <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart} onDragUpdate={handleDragUpdate}>
                <Droppable droppableId="ROOT" type="COLUMN" direction="HORIZONTAL">
                    {(provided) => (
                        <Grid container spacing={2} {...provided.droppableProps} ref={provided.innerRef}>
                            {columnsOrder.map((colId, index) => {
                                const columnData = data[colId];
                                return (
                                    <Grid item xs={12} key={columnData.id} sx={{ position: "relative" }}>
                                        <Column
                                            {...columnData}
                                            ITEMS={items}
                                            choise={choise}
                                            onChangeColumn={onChangeColumn}
                                            onChangeOrder={onChangeOrder}
                                            onRemoveLevel={onRemoveLevel}
                                            onAddLevel={onAddLevel}
                                            onFocused={({ event, focused }) => handleOnFocused({ event, focused })}
                                        />
                                    </Grid>
                                );
                            })}
                            {provided.placeholder}
                        </Grid>
                    )}
                </Droppable>
            </DragDropContext>
            <Collapse in={showNote}>
                <Alert
                    role={"note"}
                    severity={"info"}
                    sx={{ mt: 3 }}
                    id={"rdnd-note"}
                    aria-label={t(
                        `\n - Naciśnij spację, aby rozpocząć lub zakończyć przeciąganie elementu{{dot}} 
                        Użyj klawiszy strzałek (góra i dół) do przeciągania elementu, aby zmienić kolejność sortowania{{dot}} 
                        Naciśnij dwukrotnie klawisz escape, aby anulować dokonane przeciągnięcie{{dot}}`,
                        { dot: "." }
                    )}
                >
                    {t(
                        `Naciśnij spację, aby rozpocząć lub zakończyć przeciąganie elementu{{dot}} 
                        Użyj klawiszy strzałek (góra i dół) do przeciągania elementu, aby zmienić kolejność sortowania{{dot}} 
                        Naciśnij klawisz escape, aby anulować dokonane przeciągnięcie{{dot}}`,
                        { dot: "." }
                    )}
                </Alert>
            </Collapse>
        </Box>
    );
};

export default Board;
